import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  DollarSign,
  TrendingUp,
  Users,
  Calendar,
  Download,
  Filter,
  Building
} from 'lucide-react'
import api from '@/services/api'
import toast from 'react-hot-toast'

interface PayrollReportData {
  summary: {
    totalPayroll: number
    averageSalary: number
    totalEmployees: number
    payrollRuns: number
  }
  monthlyBreakdown: Array<{
    month: string
    payrollAmount: number
    employeeCount: number
    averageSalary: number
  }>
  departmentCosts: Array<{
    department: string
    totalCost: number
    employeeCount: number
    averageSalary: number
  }>
  taxSummary: {
    totalTax: number
    averageTaxRate: number
    byDepartment: Array<{ department: string; taxAmount: number }>
  }
  overtimeAnalysis: {
    totalOvertime: number
    averageOvertime: number
    topDepartments: Array<{ department: string; overtimeHours: number }>
  }
}

const PayrollReports: React.FC = () => {
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  })
  const [reportType, setReportType] = useState('summary')

  const { data: payrollData, isLoading } = useQuery({
    queryKey: ['payroll-reports', dateRange],
    queryFn: async (): Promise<PayrollReportData> => {
      const response = await api.get('/reports/payroll-detailed', {
        params: dateRange
      })
      return response.data.data
    }
  })

  const summaryCards = [
    {
      title: 'Total Payroll',
      value: `$${(payrollData?.summary.totalPayroll || 0).toLocaleString()}`,
      description: 'Total payroll cost',
      icon: DollarSign,
      color: 'bg-emerald-100 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400'
    },
    {
      title: 'Average Salary',
      value: `$${(payrollData?.summary.averageSalary || 0).toLocaleString()}`,
      description: 'Per employee',
      icon: TrendingUp,
      color: 'bg-indigo-100 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400'
    },
    {
      title: 'Employees Paid',
      value: (payrollData?.summary.totalEmployees || 0).toLocaleString(),
      description: 'Active employees',
      icon: Users,
      color: 'bg-violet-100 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400'
    },
    {
      title: 'Payroll Runs',
      value: (payrollData?.summary.payrollRuns || 0).toLocaleString(),
      description: 'This period',
      icon: Calendar,
      color: 'bg-amber-100 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400'
    }
  ]

  const handleExport = async (format: 'csv' | 'pdf') => {
    try {
      const response = await api.get(`/reports/payroll-detailed/export`, {
        params: { ...dateRange, format },
        responseType: 'blob'
      })

      const blob = new Blob([response.data])
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `payroll-report-${new Date().toISOString().split('T')[0]}.${format}`
      link.click()
      window.URL.revokeObjectURL(url)

      toast.success(`Payroll report exported as ${format.toUpperCase()}`)
    } catch (error) {
      toast.error('Failed to export report')
    }
  }

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto space-y-5">
        <div className="flex items-center justify-center h-64">
          <div className="h-8 w-8 rounded-full border-[3px] border-indigo-600 border-t-transparent animate-spin" />
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto space-y-5">
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-4">
          <div className="h-10 w-10 flex items-center justify-center rounded-lg bg-indigo-600 text-white">
            <DollarSign className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Payroll Reports</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">Detailed payroll analysis and cost breakdown</p>
          </div>
        </div>
        <div className="flex items-center space-x-4 mt-4 lg:mt-0">
          <div className="flex items-center space-x-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2">
            <Filter className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
              className="text-sm border-none focus:outline-none bg-transparent text-gray-900 dark:text-white"
            />
            <span className="text-gray-400">to</span>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
              className="text-sm border-none focus:outline-none bg-transparent text-gray-900 dark:text-white"
            />
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => handleExport('csv')}
              className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              <Download className="h-4 w-4" />
              <span>CSV</span>
            </button>
            <button
              onClick={() => handleExport('pdf')}
              className="flex items-center space-x-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <Download className="h-4 w-4" />
              <span>PDF</span>
            </button>
          </div>
        </div>
      </div>

      {/* Report Type Tabs */}
      <div className="flex space-x-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1 w-fit">
        {['summary', 'department', 'tax', 'overtime'].map((type) => (
          <button
            key={type}
            onClick={() => setReportType(type)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              reportType === type
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            {type.charAt(0).toUpperCase() + type.slice(1)}
          </button>
        ))}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {summaryCards.map((card, index) => {
          const Icon = card.icon
          return (
            <div key={index} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{card.title}</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{card.value}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{card.description}</p>
                </div>
                <div className={`p-3 rounded-lg ${card.color}`}>
                  <Icon className="h-6 w-6" />
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Report Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Department Cost Breakdown */}
        {reportType === 'department' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card lg:col-span-2">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">Department Cost Breakdown</h3>
            <div className="space-y-4">
              {payrollData?.departmentCosts.map((dept, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Building className="h-5 w-5 text-gray-400" />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">{dept.department}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">{dept.employeeCount} employees</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-gray-900 dark:text-white">${dept.totalCost.toLocaleString()}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">Avg: ${dept.averageSalary.toLocaleString()}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Monthly Breakdown */}
        {reportType === 'summary' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">Monthly Payroll Trend</h3>
            <div className="space-y-4">
              {payrollData?.monthlyBreakdown.slice(-6).map((month, index) => (
                <div key={index} className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">{month.month}</div>
                  <div className="text-right">
                    <div className="font-semibold text-gray-900 dark:text-white">${month.payrollAmount.toLocaleString()}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{month.employeeCount} employees</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tax Summary */}
        {reportType === 'tax' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">Tax Summary</h3>
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-gray-400">Total Tax</span>
                <span className="text-xl font-bold text-gray-900 dark:text-white">
                  ${payrollData?.taxSummary.totalTax.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-gray-400">Average Tax Rate</span>
                <span className="text-xl font-bold text-gray-900 dark:text-white">
                  {payrollData?.taxSummary.averageTaxRate}%
                </span>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Tax by Department</h4>
                <div className="space-y-2">
                  {payrollData?.taxSummary.byDepartment.map((dept, index) => (
                    <div key={index} className="flex justify-between items-center text-sm">
                      <span className="text-gray-600 dark:text-gray-400">{dept.department}</span>
                      <span className="font-medium text-gray-900 dark:text-white">${dept.taxAmount.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Overtime Analysis */}
        {reportType === 'overtime' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">Overtime Analysis</h3>
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-gray-400">Total Overtime Cost</span>
                <span className="text-xl font-bold text-gray-900 dark:text-white">
                  ${payrollData?.overtimeAnalysis.totalOvertime.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-gray-400">Average per Employee</span>
                <span className="text-xl font-bold text-gray-900 dark:text-white">
                  ${payrollData?.overtimeAnalysis.averageOvertime.toLocaleString()}
                </span>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Top Departments</h4>
                <div className="space-y-2">
                  {payrollData?.overtimeAnalysis.topDepartments.map((dept, index) => (
                    <div key={index} className="flex justify-between items-center text-sm">
                      <span className="text-gray-600 dark:text-gray-400">{dept.department}</span>
                      <span className="font-medium text-gray-900 dark:text-white">{dept.overtimeHours} hrs</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Additional Content based on report type */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">
            {reportType === 'summary' ? 'Cost Distribution' :
             reportType === 'department' ? 'Department Comparison' :
             reportType === 'tax' ? 'Tax Efficiency' : 'Overtime Trends'}
          </h3>
          <div className="flex items-center justify-center h-48 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <DollarSign className="h-12 w-12 text-gray-400" />
            <span className="ml-2 text-gray-500 dark:text-gray-400">Chart visualization</span>
          </div>
        </div>
      </div>

      {/* Financial Insights */}
      <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-xl p-5">
        <h3 className="text-base font-semibold text-indigo-900 dark:text-indigo-300 mb-3">Financial Insights</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-indigo-800 dark:text-indigo-400">
          <div>Payroll costs increased by 8% compared to last period</div>
          <div>IT department shows highest average salary at ${payrollData?.departmentCosts.find(d => d.department === 'IT')?.averageSalary.toLocaleString()}</div>
          <div>Overtime costs represent 5.2% of total payroll</div>
          <div>Tax efficiency improved by 1.3% through optimized deductions</div>
        </div>
      </div>
    </div>
  )
}

export default PayrollReports